import type { UnfurlProgressData } from '@lib/external/unfurl';
import type { URLContentValidationResult } from './AddMediaValidationService';

/**
 * Session-scoped cache for URL unfurl scrapes, keyed by normalized URL.
 *
 * ## Why this exists
 *
 * Unfurling a YouTube channel is a long single SSE stream that can enumerate thousands of
 * videos. Before this cache, that work was owned by `UploadURLStore`, created by
 * `UploadURLProvider` mounted *inside* the Add media modal body. Both `BaseModal` and
 * `MobileModal` use `keepMounted={false}`, so closing the modal — including by an accidental
 * backdrop click — unmounted the provider, ran the context disposer, and `deinit()` aborted
 * every in-flight request. Minutes of scraping vanished with no way to recover, and merely
 * switching the destination library did the same via a `key` remount.
 *
 * Living outside the React tree is the whole point: nothing can unmount this, so a scrape
 * outlives any number of modal open/close cycles.
 *
 * ## Deliberately NOT persisted
 *
 * In-memory only. No localStorage, no IndexedDB, no cross-tab sync, no survival across a page
 * refresh. Remembering within the session is all that was asked for; persistence would have
 * dragged in TTL sweeping, size caps, quota handling and cross-tab liveness heartbeats to
 * serve a case nobody wanted.
 *
 * ## Aborting
 *
 * There is no canceller, and unsubscribing never aborts. A scrape always runs to completion,
 * even with nothing listening — that is exactly what lets a user close the modal, or fix a
 * typo they just made in the URL, and re-attach to the same in-flight work instead of
 * restarting it. The tradeoff is that an abandoned scrape finishes anyway; it's bounded by
 * the session.
 *
 * ## Why the scanner is injected
 *
 * The real scanner reaches `AddMediaValidationService`, which transitively imports React
 * components and SCSS modules — unloadable by the standalone `npx tsx` test runner this repo
 * uses. Taking it as a constructor argument keeps this module's imports type-only (and so
 * fully erased at runtime), which is what makes the cache testable at all. See
 * `unfurlCache.ts` for the wired-up production singleton.
 */

/** Progress/result state for one URL. */
export type UnfurlCacheRecord = {
  /** Normalized URL — the cache key. */
  url: string;
  /** Entities seen so far. Keeps climbing while `isComplete` is false. */
  loadedCount: number;
  /** True once the scrape settled, successfully or not. */
  isComplete: boolean;
  /** Set once settled. A failed validation is still a completed record. */
  result?: URLContentValidationResult;
};

/** Performs the actual scrape. Injected so tests can supply a fake — see class docstring. */
export type UnfurlScanner = (
  url: string,
  onProgress: (progress: UnfurlProgressData) => void,
) => Promise<URLContentValidationResult>;

type LiveScan = {
  record: UnfurlCacheRecord;
  promise: Promise<URLContentValidationResult>;
};

/**
 * Cap on retained settled URLs. A session spent pasting channel after channel would otherwise
 * hold every scrape's entities in memory for the lifetime of the tab.
 *
 * Note this counts URLs, not payload size, and each record holds that scrape's entire entity
 * list — a 5,000-video channel is a few MB on its own. Kept deliberately small because the
 * behaviour this cache exists for (re-paste, reopen the modal, fix a typo) is served entirely
 * by the most recent handful. If retention ever becomes a problem, budget on summed
 * `loadedCount` rather than raising this.
 */
export const MAX_CACHED_URLS = 5;

/**
 * Normalizes a URL into a cache key.
 *
 * Deliberately shallow — this exists so re-pasting the same link with a stray trailing slash
 * or a `www.` still hits the cache. It is not trying to canonicalize every equivalent form of
 * a URL, and it must never merge two URLs that would unfurl differently. In particular the
 * query string is left alone: `?list=` and `?v=` change what comes back.
 */
export function normalizeUnfurlUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return '';

  try {
    const parsed = new URL(trimmed);
    parsed.hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
    // Strip a trailing slash from the path only, never from the origin.
    if (parsed.pathname.length > 1 && parsed.pathname.endsWith('/')) {
      parsed.pathname = parsed.pathname.slice(0, -1);
    }
    return parsed.toString();
  } catch {
    // Not a parseable URL — the user is probably mid-type. Key it as-is so an entry still
    // dedupes against itself.
    return trimmed;
  }
}

export class UnfurlScrapeCache {
  /** Settled scrapes. Insertion-ordered, so the least-recently-used is evicted first. */
  private completed: Map<string, UnfurlCacheRecord> = new Map();

  /** In-flight scrapes. Never evicted — a live scan is always worth keeping. */
  private live: Map<string, LiveScan> = new Map();

  /**
   * Listeners keyed by URL, held independently of `live` so that subscribing before, during,
   * or after a scan all behave the same. Coupling them to the live scan made call order
   * matter, which is a trap not worth leaving lying around.
   */
  private listeners: Map<string, Set<(record: UnfurlCacheRecord) => void>> = new Map();

  constructor(private readonly scanner: UnfurlScanner) {}

  /**
   * Synchronous lookup. Being sync is what lets a caller hydrate a re-pasted URL in the same
   * tick — no spinner flash, no debounce.
   *
   * Returns the live record (with its current `loadedCount`) if a scrape is running, the
   * settled record if one finished, or `null` on a miss.
   */
  getRecord(url: string): UnfurlCacheRecord | null {
    const key = normalizeUnfurlUrl(url);
    if (!key) return null;
    return this.readRecord(key);
  }

  /** `getRecord` for callers that have already normalized, so the URL isn't re-parsed. */
  private readRecord(key: string): UnfurlCacheRecord | null {
    const found = this.live.get(key)?.record ?? this.completed.get(key) ?? null;
    return found ? { ...found } : null;
  }

  /**
   * Listens for progress on a URL, firing immediately with the current state if there is any.
   * Returns an unsubscribe function. Unsubscribing does NOT abort — see class docstring.
   */
  subscribe(url: string, onUpdate: (record: UnfurlCacheRecord) => void): () => void {
    const key = normalizeUnfurlUrl(url);
    if (!key) return () => {};

    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(onUpdate);

    const current = this.readRecord(key);
    // Guarded like `notify` does: a subscriber that throws on its initial state must not take
    // out the caller that was merely registering it.
    if (current) {
      try {
        onUpdate(current);
      } catch (error) {
        console.error('UnfurlScrapeCache subscriber threw:', error);
      }
    }

    return () => {
      const active = this.listeners.get(key);
      if (!active) return;
      active.delete(onUpdate);
      if (active.size === 0) this.listeners.delete(key);
    };
  }

  /**
   * Starts a scrape, joins one already in progress, or resolves straight from a settled
   * record. Safe to call repeatedly for the same URL — it never starts duplicate work.
   */
  start(url: string): Promise<URLContentValidationResult> {
    const key = normalizeUnfurlUrl(url);
    if (!key) {
      return Promise.resolve({ valid: false, reason: 'Please enter a valid URL to upload from.' });
    }

    const live = this.live.get(key);
    if (live) return live.promise;

    const settled = this.completed.get(key);
    if (settled?.result) {
      // Refresh recency so a URL the user keeps coming back to isn't first to be evicted.
      this.completed.delete(key);
      this.completed.set(key, settled);
      return Promise.resolve(settled.result);
    }

    const record: UnfurlCacheRecord = { url: key, loadedCount: 0, isComplete: false };
    // Defer the scan by a microtask so `this.live` is already consistent before any of the
    // scan body — including its terminal bookkeeping — can run.
    const promise = Promise.resolve().then(() => this.runScan(key, url, record));
    this.live.set(key, { record, promise });
    return promise;
  }

  private async runScan(
    key: string,
    originalUrl: string,
    record: UnfurlCacheRecord,
  ): Promise<URLContentValidationResult> {
    let result: URLContentValidationResult;
    try {
      result = await this.scanner(originalUrl, (progress) => {
        record.loadedCount = progress.loadedCount;
        this.notify(key, record);
      });
    } catch (error) {
      result = {
        valid: false,
        reason: error instanceof Error ? error.message : 'Error fetching URL info.',
      };
    }

    record.isComplete = true;
    record.result = result;

    this.live.delete(key);

    // Only successful scrapes are retained. Remembering a failure would make it permanent for
    // the session: `retryEntry` and re-pasting both route through `start()`, which would hand
    // back the cached error instead of trying again. A transient network blip must not brick a
    // URL until reload.
    if (result.valid) {
      this.completed.set(key, record);
      this.evictOverflow();
    }

    this.notify(key, record);
    return result;
  }

  private notify(key: string, record: UnfurlCacheRecord) {
    const set = this.listeners.get(key);
    if (!set?.size) return;

    // Hand out a copy so a subscriber can't mutate cached state, and iterate a snapshot of
    // the set so unsubscribing from inside a callback is safe.
    const snapshot = { ...record };
    [...set].forEach((cb) => {
      try {
        cb(snapshot);
      } catch (error) {
        console.error('UnfurlScrapeCache subscriber threw:', error);
      }
    });
  }

  /** Drops least-recently-used settled records once over the cap. */
  private evictOverflow() {
    while (this.completed.size > MAX_CACHED_URLS) {
      const oldestKey = this.completed.keys().next().value;
      if (oldestKey === undefined) break;
      this.completed.delete(oldestKey);
    }
  }

  /**
   * Drops all cached state. Currently only used by tests.
   *
   * Worth knowing: unlike every other module-scope holder in this app, this cache is NOT
   * cleared on logout, so an SPA navigation carries it into the next account — the next user's
   * paste can hydrate from the previous user's scrape. Low impact (unfurl targets are public
   * URLs) and deliberately left alone, because importing this into `User.logout` would add a
   * `User → unfurlCache → AddMediaValidationService → unfurl → api → User` import cycle to a
   * graph that already carries a "be careful" warning. See `.claude/scratchpad/TODO.md`.
   */
  reset() {
    this.completed.clear();
    this.live.clear();
    this.listeners.clear();
  }
}
